import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react'

import { FloatMessage } from './../Commons/FloatMessage'


const FloatMsgCtx = createContext(null)

export const MESSAGE_TYPE = {
  SUCCESS: 1,
  BAD: 2,
  INFO: 3,
  WARNING: 4,
}

export function FloatMessageProvider({ children, defaultDuration = 5000 }) {
  const [msg, setMsg] = useState(null); // { id, type, text, closing }
  const timerRef = useRef(null);
  const durationRef = useRef(defaultDuration);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Cierra con animación de salida
  const closeMessage = useCallback(() => {
    setMsg((m) => (m ? { ...m, closing: true } : m));
  }, []);

  // Muestra un nuevo mensaje (reemplaza si existe)
  // Soporta tanto showMessage({ type, text, duration }) como showMessage(text, type, duration)
  const showMessage = useCallback((arg1, arg2, arg3) => {
    let type = MESSAGE_TYPE.INFO;
    let text = '';
    let duration = defaultDuration;

    if (arg1 && typeof arg1 === 'object') {
      type = arg1.type ?? MESSAGE_TYPE.INFO;
      text = arg1.text ?? '';
      duration = arg1.duration ?? defaultDuration;
    } else if (typeof arg1 === 'string') {
      text = arg1;
      type = arg2 ?? MESSAGE_TYPE.INFO;
      duration = arg3 ?? defaultDuration;
    }

    clearTimer();
    durationRef.current = Math.max(1000, duration);

    // Si hay uno visible, lo reemplazamos “en seco”
    setMsg(null);
    // microtick para reiniciar la animación de entrada
    setTimeout(() => {
      setMsg({ id: Date.now(), type, text, closing: false });
      timerRef.current = setTimeout(() => closeMessage(), durationRef.current);
    }, 0);
  }, [closeMessage, defaultDuration]);

  // Limpieza al desmontar
  useEffect(() => clearTimer, []);

  const value = { showMessage, closeMessage, MESSAGE_TYPE };

  return (
    <FloatMsgCtx.Provider value={value}>
      {children}
      {/* Capa visual del mensaje */}
      <FloatMessage
        msg={msg}
        onCloseClick={() => {
          clearTimer();
          closeMessage();
        }}
        onExitAnimationEnd={() => {
          // Cuando termina la animación de salida, removemos el mensaje
          setMsg(null);
        }}
      />
    </FloatMsgCtx.Provider>
  );
}

export function useFloatMessage() {
  const ctx = useContext(FloatMsgCtx);
  if (!ctx) throw new Error("useFloatMessage debe usarse dentro de <FloatMessageProvider>");
  return ctx;
}